import React, { useState, useEffect } from "react";
import PageHeading from "../../components/PageHeading";
import { Link, useNavigate } from 'react-router-dom';
import {
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  DingtalkCircleFilled,
} from "@ant-design/icons";
import axiosInstance from "../../utils/axios";
import { Button, Input, message, Popconfirm, Table, Tag } from "antd";
import { debounce } from "lodash";
import EventModal from "./EventModal";

const Events = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState({
    current_page: 1,
    page_size: 20,
    results: [],
  });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  useEffect(() => {
    const delayFetch = debounce(() => {
      fetchData();
    }, 550); // 550ms debounce

    delayFetch();

    return () => delayFetch.cancel();

    // eslint-disable-next-line
  }, [search, page]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const openModalCreate = () => {
    setSelectedId(null);
    setCreateModalOpen(true);
  };

  const openModalUpdate = (id) => {
    setSelectedId(id);
    setCreateModalOpen(true);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `news_and_events/events/?page=${page}&search=${search}`,
      );
      setEvents(response.data);
    } catch (error) {
      message.error("Failed to fetch events");
    } finally {
      setLoading(false);
    }
  };

  const cancel = () => message.info("Cancelled");

  const confirm = async (id) => {
    try {
      await axiosInstance.delete(`news_and_events/events/${id}/`);
      message.success("Deleted successfully");
      setPage(1);
      fetchData();
    } catch (err) {
      message.error("Cannot delete");
    }
  };

  const columns = [
    {
      title: "Heading",
      dataIndex: "heading",
      key: "heading",
      render: (text) => {
        const minLength = 15;
        const shouldTruncate = text.length > minLength;
        const displayText = shouldTruncate
          ? text.slice(0, minLength) + "..."
          : text;

        return <span title={shouldTruncate ? text : ""}>{displayText}</span>;
      },
    },
    {
      title: "Start Date",
      dataIndex: "start_date",
      key: "start_date",
    },
    {
      title: "End Date",
      dataIndex: "end_date",
      key: "end_date",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (_, record) =>
        record.publish === true ? (
          <Tag color="success">Published</Tag>
        ) : (
          <Tag color="warning">Hidden</Tag>
        ),
    },
    {
      title: "Action",
      dataIndex: "",
      key: "actions",
      render: (_, record) => (
        <>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => openModalUpdate(record.id)}
          />
          <Popconfirm
            title="Delete"
            description="Are you sure to delete this item?"
            onConfirm={() => confirm(record.id)}
            onCancel={cancel}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <div className="dashboard">
      <PageHeading name="Events" icon={<DingtalkCircleFilled />} />

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "12px",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <Input
          placeholder="Search here"
          prefix={<SearchOutlined />}
          style={{ width: "190px" }}
          onChange={(e) => handleSearch(e)}
        />
        <Button type="primary" onClick={() => openModalCreate()}>
          Add new
        </Button>
        <Button 
          onClick={() => navigate('/admin/events/add-staff')}
          style={{ marginLeft: 8 }}
        >
          Manage Event Staff
        </Button>
      </div>

      <Table
        dataSource={events.results}
        loading={loading}
        columns={columns}
        rowKey="id"
        scroll={{ x: "max-content" }}
        pagination={{
          current: page,
          pageSize: 20,
          total: events.count,
          onChange: (newPage, _) => {
            setPage(newPage);
          },
        }}
      />

      <EventModal
        open={createModalOpen}
        setOpen={setCreateModalOpen}
        selectedId={selectedId}
        refreshFetch={fetchData}
      />
    </div>
  );
};

export default Events;
